import { axiosCore } from './http-core-service';
import { PasswordCriterionsDto } from './dtos/password-criterions.dto';
import { SubscriberAccountDto } from './dtos/subscriber-account.dto';
import { ValidationDto } from './dtos/validation.dto';
import { UserDto } from './dtos/user.dto';
import { UpdateAccountDto } from './dtos/update-account.dto';
import { PasswordDto } from './dtos/form/password.dto';
import { SearchUserFormDto } from './dtos/form/search-user-form.dto';
import { env } from '../env';
import { UserAdminPaginationDto } from './dtos/user-admin-pagination.dto';

const statutOK = 200;
const urlCompteAbonne = '/compte_abonnes/';

/**
 * Méthode qui récupère les critères des mots de passe
 * @return les critères
 */
export const getPasswordCriterions = async (): Promise<PasswordCriterionsDto> => {
    const response = await axiosCore.get('/mot_de_passe/criteres');
    return response.data;
};

/**
 * Méthode qui valide les mots de passe
 * @return si valide
 */
export const isValidPassword = async (password: string): Promise<string[]> => {
    // Création d'un json pour la requête
    let data = {
        password: password,
    };
    const response = await axiosCore.post('/mot_de_passe/verification', data);
    return response.data;
};

/**
 * Méthode créer un compte abonné
 * @return si valide
 */
export const createAccount = async (data: SubscriberAccountDto): Promise<any> => {
    const response = await axiosCore.post('/compte_abonnes', data);
    return response.data;
};

/**
 * Méthode qui valide le compte créé
 * @return si valide
 */
export const validateAccount = async (token: string | undefined): Promise<ValidationDto> => {
    const response = await axiosCore.post('/compte_abonnes/activation/' + token);
    const success = response.status === statutOK;
    return {
        success: success,
        message: response.data.message,
    };
};

/**
 * Récupération des informations d'un compte
 * @param id identifiant du compte
 */
export const getUser = async (id: string): Promise<UserDto> => {
    const response = await axiosCore.get(urlCompteAbonne + id);
    response.data.utilisateur.identifiant = response.data.identifiant;
    return response.data.utilisateur;
};

/**
 * Mise à jour d'un compte
 * @return si valide
 */
export const updateAccount = async (data: UpdateAccountDto, id: string): Promise<any> => {
    const response = await axiosCore.patch(urlCompteAbonne + id, data);
    return response.data;
};

/**
 * Mise à jour d'un compte
 * @return si valide
 */
export const updatePassword = async (data: PasswordDto): Promise<any> => {
    const response = await axiosCore.patch('/compte_abonnes/changement_mot_de_passe', data);
    return response.data;
};

/**
 * Moteur de recherche des compte
 * @param criterions critères de recherche
 */
export const searchUser = async (criterions: SearchUserFormDto): Promise<UserAdminPaginationDto> => {
    //Construction de l'url param
    let searchParams =
        '?page=' + criterions.page + '&itemsPerPage=' + env.REACT_APP_NUMBER_ELEMENT_TABLE + '&pagination=true';
    searchParams += criterions?.identifier && '&identifiant=' + criterions.identifier + '';
    searchParams += criterions?.birthName && '&utilisateur.nomNaissance=' + criterions.birthName + '';
    searchParams += criterions?.name && '&utilisateur.nomUsage=' + criterions.name + '';
    searchParams += criterions?.firstName && '&utilisateur.prenom=' + criterions.firstName + '';
    searchParams += criterions?.billNumber && '&numeroFacture=' + criterions.billNumber + '';
    searchParams += criterions?.contractNumber && '&numeroContrat=' + criterions.contractNumber + '';
    const response = await axiosCore.get('/compte_abonnes' + searchParams);
    return {
        users: response.data.data,
        totalItems: response.data.totalItems,
    };
};

/**
 * Récupération du token d'un abonné
 * @param id identifiant du compte
 */
export const getUserToken = async (id: string): Promise<UserDto> => {
    const response = await axiosCore.get('compte_abonnes/' + id + '/generate_token');
    return response.data.token;
};

/**
 * Deactivates an account with the given ID.
 *
 * @param {string} id - The ID of the account to be deactivated.
 * @returns {Promise<any>} - A Promise that resolves to the data returned by the server.
 *
 * @throws {Error} - If there is an error while deactivating the account.
 */
export const deactivateAccount = async (id: string): Promise<any> => {
    const response = await axiosCore.delete(urlCompteAbonne + id);
    return response.data;
};

/**
 * Fonction de récupération de l'identifiant de connexion
 *
 * @param {Object} data - Les données nécessaires pour la récupération de l'identifiant.
 * @returns {Promise} - Une promesse qui est résolue avec les données de l'identifiant récupéré.
 */
export const retrieveLogin = async (data: {
    montantDu: number;
    referenceFacture: string;
    referenceAbonnement: string;
    captcha: string;
}): Promise<any> => {
    const response = await axiosCore.post('/compte_abonnes/recuperation_identifiant', data);
    return response.data;
};
